import React, { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { useToast, Box, Stack, HStack, Button } from "@chakra-ui/react";
import {
  getFilmPrimaryData,
  getSuggestionDataDataAPI,
  updateFilmPrimaryData,
  getResearchCategoriesAPI,
} from "../services/Films";
import { fetchMasterlistSubTypes } from "../util/getMasterlistSubType";
import Form from "@rjsf/chakra-ui";
import validator from "@rjsf/validator-ajv8";
import TypeaheadWidget from "../CommonComponents/TypeaheadWidget";
import {
  getDeletedValues,
  reverseTransformData,
  transformData,
} from "../util/helper";
import FormLoader from "../CommonComponents/FormLoader";
import {
  getApiErrorMessage,
  showErrorToast,
  showSuccessToast,
} from "../util/errorHandler";
import {
  homeCollageOrderSchema,
  homeCollageOrderUiSchema,
} from "../constants/homeCollage";
import { parseHomeCollageConflict } from "../util/homeCollageConflict";
import { useHomeCollageReassign } from "../hooks/useHomeCollageReassign";
import HomeCollageReassignDialog from "../CommonComponents/HomeCollageReassignDialog";
import {
  exploreCarouselOrderSchema,
  exploreCarouselOrderUiSchema,
} from "../constants/exploreCarousel";
import {
  exploreSearchHintOrderSchema,
  exploreSearchHintOrderUiSchema,
  exploreSearchHintKindSchema,
  exploreSearchHintKindUiSchema,
  useSearchHintPoolSize,
} from "../constants/exploreSearchHints";
/**
 * Trims each string in an array and removes empty/whitespace-only entries.
 * Always returns an array — never sends empty strings to the API.
 *
 * @param {any} arr - Value to clean (should be an array of strings).
 * @returns {string[]} Cleaned array.
 *
 * Scale Analysis: O(n) over the number of array items. For typical AKA/tolerance
 * arrays (< 50 items) this is negligible; safe at any realistic data volume.
 */
const cleanArray = (arr) => {
  if (!Array.isArray(arr)) return [];
  return arr
    .map((v) => (typeof v === "string" ? v.trim() : v))
    .filter((v) => v !== "" && v !== null && v !== undefined);
};

// Left Column Schema - Identity & Basic Info
const getLeftColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    alias: {
      type: "string",
      title: "Alias",
    },
    meta_title: {
      type: "string",
      title: "Meta Title",
    },
    meta_description: {
      type: "string",
      title: "Meta Description",
    },
    title: {
      type: "string",
      title: "Title",
    },
    title_in_original_language_font: {
      type: "string",
      title: "Title in Original Language Font",
    },
    title_in_original_language: {
      type: "string",
      title: "Title in Original Language",
    },
    title_with_year: {
      type: "string",
      title: "Title with Year",
    },
    sl_no: {
      type: "string",
      title: "Serial Number",
    },
    subtitle: {
      type: "string",
      title: "Subtitle",
    },
    aka: {
      type: "array",
      title: "AKA",
      items: {
        type: "string",
      },
    },
    aka_typo_tolerance: {
      type: "array",
      title: "AKA Typo Tolerance",
      items: {
        type: "string",
      },
    },
    subject: {
      type: "string",
      title: "Masterlist Type",
    },
    masterlist_type: {
      type: "string",
      title: "Masterlist Sub Type",
    },
    researchCategories: {
      type: "array",
      title: "Research Category",
      items: {
        type: "object",
        properties: {
          alias: {
            type: "string",
            title: "Alias",
          },
        },
      },
    },
    film_movement: {
      type: "string",
      title: "Film Movement",
    },
    masterlist_alias: {
      type: "string",
      title: "Masterlist Alias",
    },
  };

  return {
    description: "",
    type: "object",
    properties: baseProperties,
    required: ["alias", "title", "subject", "masterlist_type"],
  };
};

// Right Column Schema - Metadata & Details
const getRightColumnSchema = (hasIsExplore) => {
  const baseProperties = {
    image_alias: {
      type: "string",
      title: "Image Alias",
    },
    film_title_image_alias: {
      type: "string",
      title: "Film Title Font Image",
    },
    booklet_art_image_alias: {
      type: "array",
      title: "Booklet Art Image Alias",
      items: {
        type: "string",
      },
    },
    release_date: {
      type: ["string", "null"],
      title: "Release Date",
      default: null,
    },
    first_release_location: {
      type: "string",
      title: "Location of First Release",
    },
    sortable_day: {
      type: "string",
      title: "Sortable Day",
    },
    sortable_month: {
      type: "string",
      title: "Sortable Month",
    },
    sortable_year: {
      type: "string",
      title: "Sortable Year",
    },
    bibliographic_citation: {
      type: "string",
      title: "Bibliographic Citation",
      readOnly: true,
    },
    bibliographic_caption: {
      type: "string",
      title: "Bibliographic Caption",
      readOnly: true,
    },
    remarks_on_film: {
      type: "string",
      title: "Remarks on Film",
    },
    internal_notes_on_film: {
      type: "string",
      title: "Internal Notes on Film",
    },
    source_of_censor_certification: {
      type: "string",
      title: "Source of Censor Certification",
    },
    source_of_reference: {
      type: "string",
      title: "Source of Reference",
    },
    source_alias: {
      type: "string",
      title: "Source Alias",
    },
    booklet_text: {
      type: "string",
      title: "Booklet Text",
    },
    trailer_link: {
      type: "string",
      title: "Trailer Link",
    },
    credit_cast_default_view: {
      type: "string",
      title: "Credit Cast Default View",
      enum: ["cast", "full_credits"],
      default: "full_credits",
    },
    isExplore: {
      type: "boolean",
      title: "Is Explore",
      default: false,
    },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseProperties["display_name"] = {
      type: "string",
      title: "Display Name",
    };
    baseProperties["sort_name"] = {
      type: "string",
      title: "Sort Name",
    };
    baseProperties["route_type"] = {
      type: "string",
      title: "Route",
    };
    baseProperties["image"] = {
      type: "string",
      title: "Explore Image URL",
    };
    baseProperties["home_collage_order"] = homeCollageOrderSchema();
    baseProperties["explore_carousel_order"] = exploreCarouselOrderSchema();
    baseProperties["explore_search_hint_order"] =
      exploreSearchHintOrderSchema();
    baseProperties["explore_search_hint_kind"] = exploreSearchHintKindSchema();
  }

  const requiredFields = [];
  if (hasIsExplore) {
    requiredFields.push("route_type");
    requiredFields.push("image");
  }

  return {
    description: "",
    type: "object",
    properties: baseProperties,
    required: requiredFields,
  };
};

// Combined Schema for Single Column (Preview Mode)
const getCombinedSchema = (hasIsExplore) => {
  const leftSchema = getLeftColumnSchema(hasIsExplore);
  const rightSchema = getRightColumnSchema(hasIsExplore);

  return {
    title: "Primary fields",
    description: "",
    type: "object",
    properties: {
      ...leftSchema.properties,
      ...rightSchema.properties,
    },
    required: [...leftSchema.required, ...rightSchema.required],
  };
};

// Left Column UI Schema
const getLeftColumnUiSchema =
  (hasIsExplore, hasSubjectValue) => (isSubmitting) => {
    return {
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:style": {
        fontSize: "14px",
      },
      alias: {
        "ui:readonly": true,
        "ui:disabled": true,
      },
      meta_title: {
        "ui:emptyValue": "",
      },
      meta_description: {
        "ui:widget": "textarea",
        "ui:emptyValue": "",
      },
      typeaheadField: {
        "ui:widget": "typeahead",
      },
      researchCategories: {
        items: {
          alias: {
            "ui:widget": "typeahead1",
          },
        },
      },
      subject: {
        "ui:readonly": hasSubjectValue,
        "ui:disabled": hasSubjectValue,
        "ui:emptyValue": "",
      },
      masterlist_type: {
        "ui:widget": "typeahead3",
      },
      title: { "ui:emptyValue": "" },
      title_in_original_language_font: { "ui:emptyValue": "" },
      title_in_original_language: { "ui:emptyValue": "" },
      title_with_year: { "ui:emptyValue": "" },
      sl_no: { "ui:emptyValue": "" },
      subtitle: { "ui:emptyValue": "" },
      aka: { "ui:emptyValue": [] },
      aka_typo_tolerance: { "ui:emptyValue": [] },
      film_movement: { "ui:emptyValue": "" },
      masterlist_alias: { "ui:emptyValue": "" },
    };
  };

// Right Column UI Schema
const getRightColumnUiSchema = (hasIsExplore) => (isSubmitting) => {
  const baseUiSchema = {
    "ui:submitButtonOptions": {
      norender: true,
    },
    "ui:style": {
      fontSize: "14px",
    },
    image_alias: { "ui:emptyValue": "" },
    booklet_art_image_alias: { "ui:emptyValue": [] },
    film_title_image_alias: { "ui:emptyValue": "" },
    release_date: {
      "ui:emptyValue": "",
    },
    first_release_location: { "ui:emptyValue": "" },
    sortable_day: { "ui:emptyValue": "" },
    sortable_month: { "ui:emptyValue": "" },
    sortable_year: { "ui:emptyValue": "" },
    bibliographic_citation: { "ui:emptyValue": "" },
    bibliographic_caption: { "ui:emptyValue": "" },
    remarks_on_film: { "ui:emptyValue": "" },
    internal_notes_on_film: { "ui:emptyValue": "" },
    source_of_censor_certification: { "ui:emptyValue": "" },
    source_of_reference: { "ui:emptyValue": "" },
    source_alias: { "ui:emptyValue": "" },
    booklet_text: { "ui:emptyValue": "" },
    trailer_link: { "ui:emptyValue": "" },
    credit_cast_default_view: {
      "ui:widget": "radio",
      "ui:enumNames": ["Cast", "Full Credits"],
    },
    isExplore: { "ui:emptyValue": false },
  };

  // Add isExplore fields only if isExplore is true
  if (hasIsExplore) {
    baseUiSchema.display_name = { "ui:emptyValue": "" };
    baseUiSchema.sort_name = { "ui:emptyValue": "" };
    baseUiSchema.route_type = { "ui:emptyValue": "" };
    baseUiSchema.image = { "ui:emptyValue": "" };
    baseUiSchema.home_collage_order = homeCollageOrderUiSchema();
    baseUiSchema.explore_carousel_order = exploreCarouselOrderUiSchema();
    baseUiSchema.explore_search_hint_order = exploreSearchHintOrderUiSchema();
    baseUiSchema.explore_search_hint_kind = exploreSearchHintKindUiSchema();
  }

  return baseUiSchema;
};

// Combined UI Schema
const getCombinedUiSchema =
  (hasIsExplore, hasSubjectValue) => (isSubmitting) => {
    const leftUi = getLeftColumnUiSchema(
      hasIsExplore,
      hasSubjectValue,
    )(isSubmitting);
    const rightUi = getRightColumnUiSchema(hasIsExplore)(isSubmitting);

    return {
      ...leftUi,
      ...rightUi,
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:style": {
        fontSize: "14px",
      },
    };
  };

const Primary = () => {
  const { alias } = useParams();
  const [leftFormData, setLeftFormData] = useState({});
  const [rightFormData, setRightFormData] = useState({});
  const [combinedFormData, setCombinedFormData] = useState({});

  const [primaryFieldData, setPrimaryFieldData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [quickSave, setQuickSave] = useState(false);
  const homeCollageReassign = useHomeCollageReassign();
  const toast = useToast();
  // Re-renders once the live hint pool size (app_configs) is known.
  useSearchHintPoolSize();

  const previewEnabled = useSelector((state) => state.preview.isEnabled);
  const leftFormRef = useRef(null);
  const rightFormRef = useRef(null);
  const combinedFormRef = useRef(null);

  const fetchOptions = async (inputValue) => {
    const result = await getSuggestionDataDataAPI(inputValue);
    return result?.data?.map((item) => ({
      label: item.title,
      value: item.title,
    }));
  };

  // Function for create options
  const transformOptions = (data) => {
    return data?.map((itemArray) => {
      // Convert the array of key-value pairs into a single object
      const entity = itemArray.reduce((acc, { key, value }) => {
        acc[key] = value;
        return acc;
      }, {});

      // Use the `alias` field for label and value
      return {
        label: entity.alias,
        value: entity.alias,
      };
    });
  };

  const fetchCategories = async (inputValue) => {
    const result = await getResearchCategoriesAPI();
    const transformedOptions = transformOptions(result);

    // This function return the options
    return transformedOptions.filter((option) =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()),
    );
  };

  const splitFormData = (data = {}) => {
    // Define field groups based on validation and logical grouping
    const leftFields = [
      "alias",
      "meta_title",
      "meta_description",
      "title",
      "title_in_original_language_font",
      "title_in_original_language",
      "title_with_year",
      "sl_no",
      "subtitle",
      "aka",
      "aka_typo_tolerance",
      "subject",
      "masterlist_type",
      "researchCategories",
      "film_movement",
      "masterlist_alias",
    ];

    // All other fields go to right form (implicitly or explicitly)
    // But we should be explicit to avoid data loss
    const rightFields = [
      "image_alias",
      "film_title_image_alias",
      "booklet_art_image_alias",
      "release_date",
      "first_release_location",
      "sortable_day",
      "sortable_month",
      "sortable_year",
      "bibliographic_citation",
      "bibliographic_caption",
      "remarks_on_film",
      "internal_notes_on_film",
      "source_of_censor_certification",
      "source_of_reference",
      "source_alias",
      "booklet_text",
      "trailer_link",
      "credit_cast_default_view",
      "isExplore",
      // isExplore dependent fields
      "display_name",
      "sort_name",
      "route_type",
      "image",
      "home_collage_order",
      "explore_carousel_order",
      "explore_search_hint_order",
      "explore_search_hint_kind",
    ];

    const leftData = {};
    const rightData = {};

    leftFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        leftData[field] = data[field];
      } else {
        // Set defaults
        if (
          field === "aka" ||
          field === "aka_typo_tolerance" ||
          field === "researchCategories"
        )
          leftData[field] = [];
        else leftData[field] = "";
      }
    });

    rightFields.forEach((field) => {
      if (data.hasOwnProperty(field)) {
        rightData[field] = data[field];
      } else {
        // Set defaults
        if (field === "booklet_art_image_alias") rightData[field] = [];
        else if (field === "isExplore") rightData[field] = false;
        else if (field === "release_date") rightData[field] = null;
        else if (field === "credit_cast_default_view")
          rightData[field] = "full_credits";
        else if (
          field === "home_collage_order" ||
          field === "explore_carousel_order" ||
          field === "explore_search_hint_order"
        )
          rightData[field] = null;
        else rightData[field] = "";
      }
    });

    return { leftData, rightData };
  };

  const combineFormData = (left, right) => {
    return { ...left, ...right };
  };

  // Check if isExplore is true
  const hasIsExplore = useMemo(() => {
    const dataToCheck = previewEnabled ? combinedFormData : rightFormData;
    return dataToCheck?.isExplore === true;
  }, [combinedFormData, rightFormData, previewEnabled]);

  const hasSubjectValue = useMemo(() => {
    return !!(
      primaryFieldData?.subject &&
      String(primaryFieldData.subject).trim() !== ""
    );
  }, [primaryFieldData]);

  const leftColumnSchema = useMemo(
    () => getLeftColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const rightColumnSchema = useMemo(
    () => getRightColumnSchema(hasIsExplore),
    [hasIsExplore],
  );
  const combinedSchema = useMemo(
    () => getCombinedSchema(hasIsExplore),
    [hasIsExplore],
  );

  const leftColumnUiSchema = useMemo(
    () => getLeftColumnUiSchema(hasIsExplore, hasSubjectValue)(isSubmitting),
    [hasIsExplore, hasSubjectValue, isSubmitting],
  );
  const rightColumnUiSchema = useMemo(
    () => getRightColumnUiSchema(hasIsExplore)(isSubmitting),
    [hasIsExplore, isSubmitting],
  );
  const combinedUiSchema = useMemo(
    () => getCombinedUiSchema(hasIsExplore, hasSubjectValue)(isSubmitting),
    [hasIsExplore, hasSubjectValue, isSubmitting],
  );

  const widgets = {
    typeahead: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchOptions} />
    ),
    typeahead1: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchCategories} />
    ),
    typeahead3: (props) => (
      <TypeaheadWidget {...props} fetchOptions={fetchMasterlistSubTypes} />
    ),
  };

  // Fetch initial data
  useEffect(() => {
    const getPrimaryData = async () => {
      setIsLoading(true);
      try {
        if (alias) {
          const result = await getFilmPrimaryData(alias);

          const fieldsToTransform = [
            "ssb_image_alias",
            "image_alias",
            "booklet_art_image_alias",
            "film_title_image_alias",
          ];

          if (result) {
            const transformedData = transformData(result, fieldsToTransform);

            // Ensure array fields are always arrays
            // Read from API key (aka_type_tolerance) and store under form state key (aka_typo_tolerance)
            const arrayFields = [
              "booklet_art_image_alias",
              "aka",
              "researchCategories",
            ];
            arrayFields.forEach((field) => {
              if (!Array.isArray(transformedData[field])) {
                transformedData[field] = [];
              }
            });
            // Map API key → form-state key
            transformedData.aka_typo_tolerance = Array.isArray(
              transformedData.aka_type_tolerance,
            )
              ? transformedData.aka_type_tolerance
              : [];
            delete transformedData.aka_type_tolerance;
            // transformData turns every null into "", but these schemas are
            // ["integer", "null"] — "" fails validation ("must be integer,null")
            // as soon as any other field is edited. Restore null for an unset slot.
            [
              "home_collage_order",
              "explore_carousel_order",
              "explore_search_hint_order",
            ].forEach((field) => {
              if (transformedData[field] === "") transformedData[field] = null;
            });

            setPrimaryFieldData(result);

            // Split data
            const { leftData, rightData } = splitFormData(transformedData);
            setLeftFormData(leftData);
            setRightFormData(rightData);
            setCombinedFormData(transformedData);
          }
        }
      } catch (error) {
        console.error("Error fetching film data:", error);
        showErrorToast(toast, "Failed to fetch film data");
      } finally {
        setIsLoading(false);
      }
    };

    getPrimaryData();
  }, [alias, toast, quickSave]);

  const handleLeftFormChange = (data) => {
    setLeftFormData(data.formData);
  };

  const handleRightFormChange = (data) => {
    setRightFormData(data.formData);
  };

  const handleCombinedFormChange = (data) => {
    setCombinedFormData(data.formData);
  };

  // Submit updated data
  const handleSubmit = async () => {
    const formData = previewEnabled
      ? combinedFormData
      : combineFormData(leftFormData, rightFormData);

    // Validate required fields explicitly for isExplore
    if (hasIsExplore) {
      if (
        !formData.route_type ||
        (typeof formData.route_type === "string" &&
          formData.route_type.trim() === "")
      ) {
        return;
      }
      if (
        !formData.image ||
        (typeof formData.image === "string" && formData.image.trim() === "")
      ) {
        return;
      }
    }

    // Clean AKA arrays before submission:
    // - Trim leading/trailing whitespace from each entry
    // - Remove empty/whitespace-only values
    // - Always send an array (even if empty) — never send empty strings (PRD §6)
    formData.aka = cleanArray(formData.aka);
    // Map form-state key (aka_typo_tolerance) back to API key (aka_type_tolerance)
    formData.aka_type_tolerance = cleanArray(formData.aka_typo_tolerance);
    delete formData.aka_typo_tolerance;

    setIsSubmitting(true);
    const fieldsToTransform = [
      "ssb_image_alias",
      "image_alias",
      "booklet_art_image_alias",
      "film_title_image_alias",
    ];

    // Ensure release_date is properly handled
    if (formData.release_date === "" || formData.release_date === undefined) {
      formData.release_date = null;
    }

    const updatedFormData = reverseTransformData(formData, fieldsToTransform);
    const deletedValues = getDeletedValues(primaryFieldData, updatedFormData);

    // Shared by the initial attempt and the confirm-reassign retry below --
    // extraPayload merges in confirm_home_collage_order_reassign: true only
    // when the admin has explicitly confirmed taking a taken slot (#266343).
    const submitFilm = (extraPayload = {}) =>
      updateFilmPrimaryData(
        alias,
        { ...updatedFormData, ...extraPayload },
        deletedValues,
      );

    try {
      if (alias) {
        const result = await submitFilm();
        if (result?.success) {
          setQuickSave((prev) => !prev);
          showSuccessToast(toast, "Data updated successfully");
        }
      }
    } catch (error) {
      const { conflict } = parseHomeCollageConflict(error);
      if (conflict) {
        // Let the admin confirm taking the slot instead of a dead-end error.
        // Confirming calls this same retry, with the flag set, so the slot
        // swap happens atomically on the backend in one call (#266343).
        homeCollageReassign.open(conflict, async () => {
          try {
            const result = await submitFilm({
              confirm_home_collage_order_reassign: true,
            });
            if (result?.success) {
              setQuickSave((prev) => !prev);
              showSuccessToast(toast, "Data updated successfully");
            }
          } catch (retryError) {
            showErrorToast(
              toast,
              getApiErrorMessage(retryError, "Failed to update film data"),
            );
            throw retryError;
          }
        });
      } else {
        console.error("Error updating film data:", error);
        showErrorToast(
          toast,
          getApiErrorMessage(error, "Failed to update film data"),
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box maxW="auto" pb="100px">
      {isLoading ? (
        <FormLoader />
      ) : (
        <Box>
          {previewEnabled ? (
            /* Single column layout for preview mode */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Box
                flex={1}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                p={4}
                borderRadius="md"
              >
                <Box fontSize="lg" fontWeight="bold" mb={4}>
                  Primary fields
                </Box>
                <Form
                  ref={combinedFormRef}
                  schema={combinedSchema}
                  uiSchema={combinedUiSchema}
                  formData={combinedFormData}
                  validator={validator}
                  onChange={handleCombinedFormChange}
                  onSubmit={(e) => {
                    if (!e.errors || e.errors.length === 0) {
                      handleSubmit();
                    }
                  }}
                  widgets={widgets}
                />
              </Box>
            </Box>
          ) : (
            /* Two column layout with separate forms */
            <Box
              display={"flex"}
              flexDirection={"row"}
              alignItems={"flex-start"}
            >
              <Stack
                flexDirection={{ base: "column", md: "row" }}
                gap={4}
                alignItems="flex-start"
                flex={1}
              >
                <HStack alignItems="flex-start" flex={1}>
                  {/* Left Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Box fontSize="lg" fontWeight="bold" mb={4}>
                      Primary fields
                    </Box>
                    <Form
                      ref={leftFormRef}
                      schema={leftColumnSchema}
                      uiSchema={leftColumnUiSchema}
                      formData={leftFormData}
                      validator={validator}
                      onChange={handleLeftFormChange}
                      onSubmit={(e) => {
                        // Left form submit is passive
                      }}
                      widgets={widgets}
                    />
                  </Box>
                  {/* Right Column */}
                  <Box
                    flex={1}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    p={4}
                    borderRadius="md"
                  >
                    <Form
                      ref={rightFormRef}
                      schema={rightColumnSchema}
                      uiSchema={rightColumnUiSchema}
                      formData={rightFormData}
                      validator={validator}
                      onChange={handleRightFormChange}
                      onSubmit={(e) => {
                        if (!e.errors || e.errors.length === 0) {
                          handleSubmit();
                        }
                      }}
                      widgets={widgets}
                    />
                  </Box>
                </HStack>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {/* Fixed Save Button at Bottom */}
      <Box
        position="fixed"
        bottom={0}
        left="250px"
        right={0}
        bg="white"
        borderTop="1px solid"
        borderColor="gray.200"
        p={4}
        zIndex={1000}
        display="flex"
      >
        <Box maxW="auto" display="flex" mx="auto">
          <Button
            colorScheme="blue"
            size="lg"
            width="auto"
            isLoading={isSubmitting}
            loadingText="Saving..."
            onClick={() => {
              const forms = document.querySelectorAll("form");

              if (forms.length > 0) {
                forms.forEach((form) => {
                  const tempSubmitBtn = document.createElement("button");
                  tempSubmitBtn.type = "submit";
                  tempSubmitBtn.style.display = "none";
                  form.appendChild(tempSubmitBtn);

                  tempSubmitBtn.click();
                  form.removeChild(tempSubmitBtn);
                });
              } else {
                handleSubmit();
              }
            }}
            disabled={isSubmitting}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </Box>
      </Box>

      <Box pb="100px" />

      <HomeCollageReassignDialog
        isOpen={homeCollageReassign.isOpen}
        conflict={homeCollageReassign.conflict}
        isConfirming={homeCollageReassign.isConfirming}
        cancelRef={homeCollageReassign.cancelRef}
        onConfirm={homeCollageReassign.confirm}
        onCancel={homeCollageReassign.close}
      />
    </Box>
  );
};

export default Primary;
